import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  FaPlus,
  FaTrash,
  FaPencil,
  FaGripLines,
  FaChevronDown,
  FaChevronRight,
  FaTriangleExclamation,
} from 'react-icons/fa6';
import Badge from '@/components/Badges';
import { sourceMeta } from './constants';

const StepRow = ({ step, provided, expanded, onToggleExpand, onToggleEnabled, onEdit, onDelete, busy }) => {
  const source = sourceMeta(step.source);

  return (
    <div
      ref={provided?.innerRef}
      {...(provided?.draggableProps || {})}
      className="mb-3"
      style={provided?.draggableProps?.style}
    >
      <div
        className={`flex items-stretch bg-white dark:bg-theme-surface-accent border rounded-lg overflow-hidden ${
          step.enabled
            ? 'border-slate-200 dark:border-slate-700'
            : 'border-amber-300 dark:border-amber-800'
        }`}
      >
        <span
          {...(provided?.dragHandleProps || {})}
          className="flex items-center justify-center px-2 text-slate-400 hover:text-slate-500 cursor-grab active:cursor-grabbing border-r border-slate-200 dark:border-slate-600"
          title="Drag to reorder"
        >
          <FaGripLines className="w-4 h-4" />
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 p-3">
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-2 min-w-0 text-left"
            >
              {expanded ? (
                <FaChevronDown className="w-3 h-3 shrink-0 text-slate-400" />
              ) : (
                <FaChevronRight className="w-3 h-3 shrink-0 text-slate-400" />
              )}
              <span className="text-xs font-mono text-slate-400 dark:text-slate-500 shrink-0">
                {step.stepOrder}
              </span>
              <span className="font-mono text-sm text-slate-800 dark:text-slate-100 truncate">
                {step.name}
              </span>
            </button>

            <div className="flex items-center gap-2 shrink-0">
              {step.isFinal && (
                <Badge color="purple" size="xs">
                  Final
                </Badge>
              )}
              <Badge color={source.color} size="xs">
                {source.label}
              </Badge>
              {step.notes && (
                <Badge color="orange" size="xs">
                  Notes
                </Badge>
              )}

              <label
                className="flex items-center gap-1.5 cursor-pointer"
                title={
                  step.enabled
                    ? 'Disabling any step drops the whole store to the column-mapping path'
                    : 'Enable this step'
                }
              >
                <input
                  type="checkbox"
                  checked={step.enabled}
                  disabled={busy}
                  onChange={(e) => onToggleEnabled(e.target.checked)}
                  className="form-checkbox"
                />
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {step.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>

              <button
                onClick={onEdit}
                title="Edit step"
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <FaPencil className="w-4 h-4" />
              </button>
              <button
                onClick={onDelete}
                title="Delete step"
                className="p-1 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400"
              >
                <FaTrash className="w-4 h-4" />
              </button>
            </div>
          </div>

          {step.warnings.length > 0 && (
            <ul className="px-3 pb-3 space-y-1">
              {step.warnings.map((warning, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300"
                >
                  <FaTriangleExclamation className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          )}

          {expanded && (
            <div className="px-3 pb-3 space-y-3">
              {step.glueJob && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Imported from Glue job <span className="font-mono">{step.glueJob}</span>
                </p>
              )}
              <div className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 max-h-96 overflow-auto">
                <pre className="text-xs text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">
                  {step.sql || '-- no SQL stored for this step'}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * A store's ordered transform chain — the part of a Glue import that carries
 * actual logic, as opposed to the column mapping's renames.
 */
const TransformStepsList = ({
  steps,
  stepsSupported = true,
  onAdd,
  onEdit,
  onDelete,
  onToggleEnabled,
  onReorder,
  busy,
}) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [reordering, setReordering] = useState(false);

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allExpanded = steps.length > 0 && steps.every((s) => expanded.has(s.id));

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const reordered = Array.from(steps);
    const [removed] = reordered.splice(from, 1);
    reordered.splice(to, 0, removed);

    setReordering(true);
    try {
      await onReorder(reordered.map((s) => s.id));
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
            Transform steps ({steps.length})
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Run in order against <code className="font-mono">source</code>, the parsed CSV. Drag to
            reorder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {steps.length > 0 && (
            <button
              onClick={() =>
                setExpanded(allExpanded ? new Set() : new Set(steps.map((s) => s.id)))
              }
              className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {allExpanded ? 'Collapse all SQL' : 'Expand all SQL'}
            </button>
          )}
          <button
            onClick={onAdd}
            disabled={!stepsSupported}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlus className="w-3.5 h-3.5 mr-1.5" />
            Add step
          </button>
        </div>
      </div>

      {!stepsSupported && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
          The <code className="font-mono">transform_steps</code> table is not present in this
          database, so every store runs the mapping-only path. Apply{' '}
          <code className="font-mono">inventory-cdc/migrations/002_transform_steps.sql</code>.
        </div>
      )}

      {steps.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
          <p className="text-slate-500 dark:text-slate-400">No transform steps for this store</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
            The pipeline runs the column-mapping path: renames only, no transformation logic.
          </p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="transform-steps" isDropDisabled={reordering || busy}>
            {(droppableProvided) => (
              <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {steps.map((step, index) => (
                  <Draggable
                    key={String(step.id)}
                    draggableId={String(step.id)}
                    index={index}
                    isDragDisabled={reordering || busy}
                  >
                    {(draggableProvided) => (
                      <StepRow
                        step={step}
                        provided={draggableProvided}
                        expanded={expanded.has(step.id)}
                        busy={busy}
                        onToggleExpand={() => toggleExpand(step.id)}
                        onToggleEnabled={(enabled) => onToggleEnabled(step, enabled)}
                        onEdit={() => onEdit(step)}
                        onDelete={() => onDelete(step)}
                      />
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default TransformStepsList;
